import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateOrganization } from '@/service-library/hooks/organizations'
import { Organization } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type FormValues = {
  name: string
  code: string
}

type AddCompanyDialogProps = {
  overlay: OverlayState
  parentOrgId: string
  listQueryKey: QueryKey
  onCreate?: (orgId: string) => void
}

export default function AddCompanyDialog({
  overlay,
  parentOrgId,
  listQueryKey,
  onCreate,
}: AddCompanyDialogProps) {
  const methods = useForm<FormValues>({
    defaultValues: { name: '', code: '' },
  })

  const {
    formState: { isDirty, isValid },
    reset,
  } = methods

  const { createOrganization } = useCreateOrganization({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to add company. Please try again.')
    },
  })

  function handleSubmit({ name, code }: FormValues) {
    const orgId = generateUuid()
    createOrganization({
      id: orgId,
      name: name.trim(),
      code: code.trim(),
      color: 'blue',
      org_type_id: null,
      org_type: null,
      parent_org_id: parentOrgId,
      is_relay_data_provider: true,
      status: 'active',
    } as Organization)
    overlay.close()
    onCreate?.(orgId)
  }

  useEffect(() => {
    if (overlay.isOpen) {
      reset({ name: '', code: '' })
    }
  }, [overlay.isOpen, reset])

  return (
    <Dialog title="New Company" {...overlay} maxWidth="xs">
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <FormTextField
              name="name"
              autoFocus
              fullWidth
              label="Name"
              required
              useControllerProps={{
                rules: {
                  validate: (value) => value.trim().length > 0,
                },
              }}
            />
            <FormTextField
              name="code"
              label="Company ID"
              fullWidth
              required
              useControllerProps={{
                rules: {
                  validate: (value) => value.trim().length > 0,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
